"use client"

import { motion } from "framer-motion"
import { Check, X } from "lucide-react"
import Link from "next/link"
import { pricingPlans } from "@/features/home/data"
import { staggerContainer, staggerItem, fadeUp, sectionLabel } from "@/lib/motion"

const VP = { once: true, amount: 0.05 }

export function PricingSection() {
  return (
    <section id="fiyatlandirma" className="relative py-16 sm:py-24 lg:py-32" aria-label="Fiyatlandırma paketleri">
      <div className="brand-divider" />
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 max-w-7xl">

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={VP}
          className="text-center max-w-3xl mx-auto mb-12 sm:mb-16"
        >
          <motion.span variants={sectionLabel} className="text-primary font-medium text-sm uppercase tracking-wider inline-block">
            Fiyatlandırma
          </motion.span>
          <motion.h2 variants={fadeUp} className="font-[family-name:var(--font-heading)] text-3xl sm:text-4xl md:text-5xl font-bold mt-4 mb-4 sm:mb-6 text-foreground">
            Projenize Uygun <span className="text-gradient-brand">Paket</span>
          </motion.h2>
          <motion.p variants={fadeUp} className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            Şeffaf fiyatlandırma. Gizli ücret yok. İhtiyacınıza göre özelleştirilebilir.
          </motion.p>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={VP}
          className="grid sm:grid-cols-2 2xl:grid-cols-4 gap-5 lg:gap-6 items-stretch"
        >
          {pricingPlans.map((plan) => (
            <motion.div
              key={plan.name}
              variants={staggerItem}
              className={`relative glass hairline flex h-full min-h-[38rem] flex-col rounded-3xl p-5 sm:p-6 transition-shadow duration-300 md:hover:-translate-y-1 hover:glow-accent ${
                plan.popular ? "ring-2 ring-primary shadow-xl shadow-primary/20 bg-primary/5" : ""
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="bg-primary text-primary-foreground text-xs font-bold px-5 py-2 rounded-full whitespace-nowrap shadow-md shadow-primary/30 ring-2 ring-primary/20">
                    ⭐ En Popüler
                  </span>
                </div>
              )}

              <div className="mb-5 min-h-[11rem] border-b border-border/70 pb-5">
                <h3 className="font-[family-name:var(--font-heading)] text-xl sm:text-2xl font-bold text-foreground mb-2 leading-tight">
                  {plan.name}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed min-h-[3.75rem] line-clamp-3">
                  {plan.description}
                </p>
                <div className="mt-5 flex min-h-12 items-end gap-1.5">
                  <span className="font-[family-name:var(--font-heading)] text-3xl font-bold tracking-tight text-foreground">
                    {plan.price}
                  </span>
                </div>
                <span className="text-muted-foreground text-xs mt-1 block">{plan.period}</span>
              </div>

              <ul className="space-y-3.5 mb-6 flex-1">
                {plan.features.map((feature) => (
                  <li key={feature.text} className="flex min-h-10 items-start gap-2.5 text-sm leading-snug">
                    {feature.included ? (
                      <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-green-500/10">
                        <Check className="w-3.5 h-3.5 text-green-600" aria-hidden="true" />
                      </span>
                    ) : (
                      <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-muted">
                        <X className="w-3.5 h-3.5 text-muted-foreground/45" aria-hidden="true" />
                      </span>
                    )}
                    <span className={feature.included ? "text-foreground" : "text-muted-foreground/50"}>
                      {feature.text}
                    </span>
                  </li>
                ))}
              </ul>

              <motion.div whileTap={{ scale: 0.97 }} className="mt-auto">
                <Link
                  href="#iletisim"
                  className={`block h-12 w-full rounded-xl px-4 text-center text-sm font-semibold leading-[3rem] transition-all duration-200 ${
                    plan.popular
                      ? "bg-primary hover:bg-primary/90 text-primary-foreground"
                      : "bg-secondary hover:bg-secondary/80 text-foreground"
                  }`}
                >
                  {plan.cta}
                </Link>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>

        <motion.p
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={VP}
          className="text-center text-muted-foreground text-sm mt-10"
        >
          Tüm paketler özelleştirilebilir. Enterprise için{" "}
          <Link href="#iletisim" className="text-primary hover:underline font-medium">
            özel teklif alın
          </Link>.
        </motion.p>

      </div>
    </section>
  )
}
